import { ROUTES } from 'common/constants';
import {
  AgricultureIcon,
  BankAccountIcon,
  BarcodeIcon,
  BoxIcon,
  CalendarClockIcon,
  CalendarIcon,
  CategoryIcon,
  ChecklistIcon,
  DashboardIcon,
  DescriptionOutlinedIcon,
  ElectronicInvoiceIcon,
  FarmStorageIcon,
  FinancialIcon,
  FinancialOverviewIcon,
  FinancialProductionIcon,
  HelpCenterIcon,
  ItemsIcon,
  MaterialConsumptionIcon,
  MovementsIcon,
  MyCropwise,
  PurchaseIcon,
  ReportIcon,
  ResourcesIcon,
  SiloIcon,
  SpeedIcon,
  SupplierIcon,
  UploadIcon,
  WhatsappIcon,
} from 'common/icons';
import { ActionRbacEnum } from 'common/utils/auth-provider.util';
import {
  COMMERCIALIZATION_MENU_DISABLE_FLAG,
  COMMERCIALIZATION_TAB_FEATURE_FLAGS,
} from 'src/pages/commercialization/commercialization.constant';
import EntitlementEnum from 'src/pages/organization/entitlements.enum';

import { FeatureItemModel } from './menu-item.model';

enum FeatureItemKeys {
  MY_CROPWISE = 'my_cropwise',
  VOICE_MANAGEMENT = 'voice_management',
  DASHBOARD = 'dashboard',
  ORDERS_AND_PAYMENTS = 'orders_and_payments',
  REVISION = 'revision',
  STOCK = 'stock',
  MATERIAL_CONSUMPTION = 'material_consumption',
  AG_OPERATION = 'ag_operation',
  REPORT_GENERAL_LEDGER = 'report_general_ledger',
  FINANCIAL = 'financial',
  RESOURCES = 'resources',
  COMMERCIALIZATION = 'commercialization',
  REPORTS = 'reports',
  HELP_CENTER = 'help_center',
}

enum ResourcesItemKeys {
  ITEM = 'item',
  MACHINERY = 'machinery',
  ITEM_CATEGORY = 'item_category',
  SUPPLIER = 'supplier',
  BANK_ACCOUNT = 'bank_account',
  IMPORT = 'import',
}

enum RevisionItemKeys {
  NO_COST_RECORDS = 'no_cost_records',
  BALANCE_AI = 'balance_ai',
}

enum FinancialItemKeys {
  OVERVIEW = 'overview',
  BUDGET = 'budget',
  PRODUCTION = 'production',
  FINANCIAL_MACHINERY = 'financial_machinery',
}

enum OrdersAndPaymentsItemKeys {
  PURCHASE = 'purchase',
  INVOICE = 'invoice',
  ACCOUNTS_PAYABLE = 'accounts_payable',
  ELECTRONIC_INVOICE = 'electronic_invoice',
}

enum CommercializationItemKeys {
  STOCK = 'warehouse',
  CONTRACTS = 'contracts',
  MOVEMENTS = 'movements',
}

const featureListData: Record<FeatureItemKeys, FeatureItemModel> = {
  [FeatureItemKeys.MY_CROPWISE]: {
    key: FeatureItemKeys.MY_CROPWISE,
    title: 'my_cropwise.title',
    description: 'my_cropwise.description',
    icon: <MyCropwise />,
    hide_on_mobile: true,
    navigate_route: ROUTES.MY_CROPWISE,
  },
  [FeatureItemKeys.VOICE_MANAGEMENT]: {
    key: FeatureItemKeys.VOICE_MANAGEMENT,
    title: 'voice_management.title',
    description: 'voice_management.description',
    icon: <WhatsappIcon />,
    hide_on_mobile: false,
    hide_on_desktop: true,
    entitlement: EntitlementEnum.BALANCE_AI_VOICE_ACTIONS,
  },
  [FeatureItemKeys.DASHBOARD]: {
    key: FeatureItemKeys.DASHBOARD,
    title: 'dashboard.title',
    description: 'reports.description',
    icon: <DashboardIcon />,
    navigate_route: ROUTES.DASHBOARD,
    feature_flag_disable: 'dashboardGuerillaFms',
    hide_on_mobile: false,
    entitlement: EntitlementEnum.LANDING_PAGE,
  },
  [FeatureItemKeys.REVISION]: {
    key: FeatureItemKeys.REVISION,
    title: 'revision.title',
    description: 'revision.description',
    icon: <ChecklistIcon />,
    hide_on_mobile: false,
    feature_flag_disable: 'balanceAiDisableMenuAccess',
    children: [
      {
        key: RevisionItemKeys.BALANCE_AI,
        title: 'revision.balance_ai.title',
        description: 'revision.balance_ai.description',
        icon: <CalendarClockIcon />,
        navigate_route: ROUTES.BALANCE_AI_LIST,
        entitlement: EntitlementEnum.BALANCE_AI_VOICE_ACTIONS,
      },
      {
        key: RevisionItemKeys.NO_COST_RECORDS,
        title: 'revision.no_cost_records.title',
        description: 'revision.no_cost_records.description',
        icon: <BoxIcon />,
        navigate_route: ROUTES.NO_COST_RECORDS,
        feature_flag_disable: 'noCostRecordsDisableMenuAccess',
      },
    ],
  },
  [FeatureItemKeys.ORDERS_AND_PAYMENTS]: {
    key: FeatureItemKeys.ORDERS_AND_PAYMENTS,
    title: 'orders_and_payments.title',
    description: 'orders_and_payments.description',
    icon: <PurchaseIcon />,
    feature_flag_disable: 'ordersAndPaymentsDisableMenuAccess',
    children: [
      {
        key: OrdersAndPaymentsItemKeys.ACCOUNTS_PAYABLE,
        title: 'accounts_payable.title',
        description: 'accounts_payable.description',
        icon: <CalendarClockIcon />,
        navigate_route: ROUTES.ACCOUNTS_PAYABLE,
        feature_flag_disable: 'accountsPayableDisableMenuAccess',
        entitlement: EntitlementEnum.ACCOUNTS_PLAYABLE,
      },
      {
        key: OrdersAndPaymentsItemKeys.PURCHASE,
        title: 'purchase.title',
        description: 'purchase.description',
        icon: <BoxIcon />,
        navigate_route: ROUTES.PURCHASE,
        feature_flag_disable: 'purchaseDisableMenuAccess',
      },
      {
        key: OrdersAndPaymentsItemKeys.INVOICE,
        title: 'invoice.title',
        description: 'invoice.description',
        icon: <BarcodeIcon />,
        navigate_route: ROUTES.INVOICE,
        feature_flag_disable: 'invoiceDisableMenuAccess',
        entitlement: EntitlementEnum.INVOICES,
      },
      {
        key: OrdersAndPaymentsItemKeys.ELECTRONIC_INVOICE,
        title: 'electronic_invoice.title',
        description: 'electronic_invoice.description',
        icon: <ElectronicInvoiceIcon />,
        navigate_route: ROUTES.ELECTRONIC_INVOICE,
        feature_flag_disable: 'electronicInvoiceDisableMenuAccess',
        entitlement: EntitlementEnum.ELECTRONIC_INVOICES_XML_IMPORT,
      },
    ],
  },
  [FeatureItemKeys.COMMERCIALIZATION]: {
    key: FeatureItemKeys.COMMERCIALIZATION,
    title: 'commercialization.title',
    description: 'commercialization.description',
    icon: <SiloIcon />,
    feature_flag_disable: COMMERCIALIZATION_MENU_DISABLE_FLAG,
    permission: ActionRbacEnum.STOCK_BALANCE_READ,
    children: [
      {
        key: CommercializationItemKeys.CONTRACTS,
        title: 'commercialization.tabs.contracts',
        description: 'commercialization.tabs.contracts',
        icon: <DescriptionOutlinedIcon />,
        navigate_route: ROUTES.COMMERCIALIZATION,
        feature_flag_disable: COMMERCIALIZATION_TAB_FEATURE_FLAGS.CONTRACTS,
      },
      {
        key: CommercializationItemKeys.STOCK,
        title: 'commercialization.tabs.stock',
        description: 'commercialization.tabs.stock',
        icon: <FarmStorageIcon />,
        navigate_route: ROUTES.COMMERCIALIZATION_STOCK,
        feature_flag_disable: COMMERCIALIZATION_TAB_FEATURE_FLAGS.STOCK,
        entitlement: EntitlementEnum.ACCOUNTS_PLAYABLE,
      },
      {
        key: CommercializationItemKeys.MOVEMENTS,
        title: 'commercialization.tabs.movements',
        description: 'commercialization.tabs.movements',
        icon: <MovementsIcon />,
        navigate_route: ROUTES.COMMERCIALIZATION_MOVEMENTS,
        feature_flag_disable: COMMERCIALIZATION_TAB_FEATURE_FLAGS.MOVEMENTS,
        entitlement: EntitlementEnum.INVOICES,
      },
    ],
  },
  [FeatureItemKeys.STOCK]: {
    key: FeatureItemKeys.STOCK,
    title: 'stock.title',
    description: 'stock.description',
    icon: <FarmStorageIcon />,
    navigate_route: ROUTES.STOCK,
    feature_flag_disable: 'stockDisableMenuAccess',
    permission: ActionRbacEnum.STOCK_BALANCE_READ,
  },
  [FeatureItemKeys.REPORT_GENERAL_LEDGER]: {
    key: FeatureItemKeys.REPORT_GENERAL_LEDGER,
    title: 'report_general_ledger.title',
    description: 'report_general_ledger.description',
    icon: <ReportIcon />,
    navigate_route: ROUTES.REPORT_GENERAL_LEDGER,
    feature_flag_disable: 'generalLedgerDisableMenuAccess',
    permission: ActionRbacEnum.FINANCIAL_BALANCE_READ,
    entitlement: EntitlementEnum.REPORT_GENERAL_LEDGER,
  },
  [FeatureItemKeys.HELP_CENTER]: {
    key: FeatureItemKeys.HELP_CENTER,
    title: 'help_center.title',
    description: 'help_center.description',
    icon: <HelpCenterIcon />,
    navigate_route: ROUTES.HELP_CENTER,
    feature_flag_disable: 'helpCenterDisableMenuAccess',
    hide_on_desktop: true,
    hide_on_mobile: true,
  },
  [FeatureItemKeys.MATERIAL_CONSUMPTION]: {
    key: FeatureItemKeys.MATERIAL_CONSUMPTION,
    title: 'material_consumption.title',
    description: 'material_consumption.description',
    icon: <MaterialConsumptionIcon />,
    navigate_route: ROUTES.MATERIAL_CONSUMPTION,
    feature_flag_disable: 'materialConsumptionDisableMenuAccess',
  },
  [FeatureItemKeys.AG_OPERATION]: {
    key: FeatureItemKeys.AG_OPERATION,
    title: 'ag_operation.title',
    description: 'ag_operation.description',
    icon: <CalendarIcon />,
    navigate_route: ROUTES.AG_OPERATION,
    feature_flag_disable: 'agOperationDisableMenuAccess',
  },
  [FeatureItemKeys.FINANCIAL]: {
    key: FeatureItemKeys.FINANCIAL,
    title: 'financial.title',
    description: 'financial.description',
    icon: <FinancialIcon />,
    feature_flag_disable: 'financialDisableMenuAccess',
    permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
    children: [
      {
        key: FinancialItemKeys.OVERVIEW,
        title: 'financial.overview.title',
        description: 'financial.description',
        icon: <FinancialOverviewIcon />,
        navigate_route: ROUTES.FINANCIAL,
        feature_flag_disable: 'financialDisableMenuAccess',
        permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
      },
      {
        key: FinancialItemKeys.BUDGET,
        title: 'financial_budget.title',
        description: 'financial.budget.description',
        icon: <SpeedIcon />,
        navigate_route: ROUTES.FINANCIAL_BUDGET,
        feature_flag_disable: 'financialBudgetDisableMenuAccess',
        permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
      },
      {
        key: FinancialItemKeys.PRODUCTION,
        title: 'financial.production',
        description: 'financial.description',
        icon: <FinancialProductionIcon />,
        navigate_route: ROUTES.FINANCIAL_PRODUCTION,
        feature_flag_disable: 'financialProductionDisableMenuAccess',
        permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
        entitlement: EntitlementEnum.PRODUCTION_YIELD_DATA,
      },
      {
        key: FinancialItemKeys.FINANCIAL_MACHINERY,
        title: 'financial.machinery.title',
        description: 'financial.machinery.description',
        icon: <AgricultureIcon />,
        navigate_route: ROUTES.FINANCIAL_MACHINERY,
        feature_flag_disable: 'financialMachineryDisableMenuAccess',
        permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
        entitlement: EntitlementEnum.PRODUCTION_YIELD_DATA,
      },
    ],
  },
  [FeatureItemKeys.RESOURCES]: {
    key: FeatureItemKeys.RESOURCES,
    title: 'resources.title',
    description: 'resources.description',
    icon: <ResourcesIcon />,
    feature_flag_disable: 'resourcesDisableMenuAccess',
    children: [
      {
        key: ResourcesItemKeys.ITEM,
        title: 'item.title',
        description: 'item.description',
        icon: <ItemsIcon />,
        navigate_route: ROUTES.ITEM,
        feature_flag_disable: 'itemDisableMenuAccess',
      },
      {
        key: ResourcesItemKeys.ITEM_CATEGORY,
        title: 'item_category.title',
        description: 'item_category.description',
        icon: <CategoryIcon />,
        navigate_route: ROUTES.ITEM_CATEGORY,
        feature_flag_disable: 'itemCategoryDisableMenuAccess',
      },
      {
        key: ResourcesItemKeys.SUPPLIER,
        title: 'supplier.title',
        description: 'supplier.description',
        icon: <SupplierIcon />,
        navigate_route: ROUTES.SUPPLIER,
        feature_flag_disable: 'supplierDisableMenuAccess',
      },
      {
        key: ResourcesItemKeys.BANK_ACCOUNT,
        title: 'bank_account.title',
        description: 'bank_account.description',
        icon: <BankAccountIcon />,
        navigate_route: ROUTES.BANK_ACCOUNT,
        feature_flag_disable: 'bankAccountsDisableMenuAccess',
        entitlement: EntitlementEnum.INVOICES,
      },
      {
        key: ResourcesItemKeys.MACHINERY,
        title: 'machinery.title',
        description: 'machinery.description',
        icon: <AgricultureIcon />,
        navigate_route: ROUTES.MACHINERY,
        feature_flag_disable: 'machineryDisableMenuAccess',
      },
      {
        key: ResourcesItemKeys.IMPORT,
        title: 'resources.import.title',
        description: 'import.description',
        icon: <UploadIcon />,
        navigate_route: ROUTES.IMPORT,
        feature_flag_disable: 'importDisableMenuAccess',
      },
    ],
  },
  [FeatureItemKeys.REPORTS]: {
    key: FeatureItemKeys.REPORTS,
    title: 'reports.title',
    description: 'reports.description',
    icon: <DescriptionOutlinedIcon />,
    navigate_route: ROUTES.REPORTS,
    feature_flag_disable: 'dataImportDisableMenuAccess',
    entitlement: EntitlementEnum.DATA_SHARING_IMPORT_EXPORT,
    permission: ActionRbacEnum.FINANCIAL_BALANCE_WRITE,
  },
};

export {
  CommercializationItemKeys,
  FeatureItemKeys,
  featureListData,
  FinancialItemKeys,
  OrdersAndPaymentsItemKeys,
  ResourcesItemKeys,
  RevisionItemKeys,
};
